import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowUpRight, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  AppShell,
  PageContainer,
  PageHeader,
  SectionContainer,
  StatCard,
  StatusBadge,
  UIButton,
} from '../../features/ui-system';
import { commandSidebarItems } from '../../features/ui-foundation/commandMenu';
import { resolveBackendBaseUrl } from '../../utils/runtimeConfig';
import {
  EXECUTION_ACTION_LABELS,
  fetchExecutionLogs,
  getExecutionActionLabel,
} from './utils/executionLogApi';

const API = `${resolveBackendBaseUrl()}/api`;

const sourceTypeOptions = [
  { value: 'all', label: 'All Sources' },
  { value: 'incident', label: 'Incident' },
  { value: 'ticket', label: 'Ticket' },
  { value: 'router', label: 'Router' },
  { value: 'customer', label: 'Customer' },
  { value: 'site', label: 'Site' },
  { value: 'manual', label: 'Manual' },
];

const actionTypeOptions = [
  { value: 'all', label: 'All Actions' },
  ...Object.keys(EXECUTION_ACTION_LABELS).map((key) => ({ value: key, label: getExecutionActionLabel(key) })),
];

const timeRangeOptions = [
  { value: 'latest', label: 'Terbaru' },
  { value: 'today', label: 'Hari ini' },
  { value: '7d', label: '7 hari terakhir' },
];

const executionActionTone = (actionType) => {
  const token = String(actionType || '').trim().toLowerCase();
  if (token === 'mitigation_completed') return 'ok';
  if (token === 'mitigation_started' || token === 'investigation_started' || token === 'backup_verified') return 'warn';
  if (token === 'ticket_created' || token === 'ticket_updated') return 'info';
  return 'neutral';
};

const sourceTypeTone = (sourceType) => {
  const token = String(sourceType || '').trim().toLowerCase();
  if (token === 'incident') return 'warn';
  if (token === 'ticket') return 'info';
  if (token === 'router' || token === 'site') return 'ok';
  return 'neutral';
};

const formatSourceLabel = (sourceType) => {
  const token = String(sourceType || '').trim().toLowerCase();
  if (!token) return 'manual';
  if (token === 'node') return 'router';
  return token;
};

const buildPrimaryCta = (entry) => {
  if (!entry) return null;
  if (entry.ticketId) return { label: 'Open in Tickets', to: `/tickets?ticketId=${encodeURIComponent(String(entry.ticketId))}` };
  if (entry.incidentId) return { label: 'Open in Recovery', to: `/recovery?incidentId=${encodeURIComponent(String(entry.incidentId))}` };
  if (entry.routerId || entry.nodeId) {
    const nodeId = String(entry.routerId || entry.nodeId || '');
    return {
      label: 'Open in Topology',
      to: `/topology?mode=live&sourceType=nas&sourceId=${encodeURIComponent(nodeId)}&routerId=${encodeURIComponent(nodeId)}&openDetail=1`,
    };
  }
  if (entry.customerId) return { label: 'View Customer', to: `/customers?customerId=${encodeURIComponent(String(entry.customerId))}` };
  if (entry.siteId) return { label: 'Open in Infrastructure', to: `/infrastructure?tab=sites&siteId=${encodeURIComponent(String(entry.siteId))}` };
  if (entry.sourceType === 'incident' && entry.sourceId) return { label: 'Open in Recovery', to: `/recovery?incidentId=${encodeURIComponent(String(entry.sourceId))}` };
  if (entry.sourceType === 'ticket' && entry.sourceId) return { label: 'Open in Tickets', to: `/tickets?ticketId=${encodeURIComponent(String(entry.sourceId))}` };
  if (entry.sourceType === 'customer' && entry.sourceId) return { label: 'View Customer', to: `/customers?customerId=${encodeURIComponent(String(entry.sourceId))}` };
  if ((entry.sourceType === 'router' || entry.sourceType === 'node') && entry.sourceId) {
    const nodeId = String(entry.sourceId || '');
    return {
      label: 'Open in Topology',
      to: `/topology?mode=live&sourceType=nas&sourceId=${encodeURIComponent(nodeId)}&routerId=${encodeURIComponent(nodeId)}&openDetail=1`,
    };
  }
  if (entry.sourceType === 'site' && entry.sourceId) return { label: 'Open in Infrastructure', to: `/infrastructure?tab=sites&siteId=${encodeURIComponent(String(entry.sourceId))}` };
  return null;
};

const buildSecondaryCtas = (entry) => {
  if (!entry) return [];
  const items = [];
  if (entry.incidentId) items.push({ label: 'Open in Recovery', to: `/recovery?incidentId=${encodeURIComponent(String(entry.incidentId))}` });
  if (entry.ticketId) items.push({ label: 'Open in Tickets', to: `/tickets?ticketId=${encodeURIComponent(String(entry.ticketId))}` });
  if (entry.customerId) items.push({ label: 'View Customer', to: `/customers?customerId=${encodeURIComponent(String(entry.customerId))}` });
  if (entry.routerId || entry.nodeId) {
    const nodeId = String(entry.routerId || entry.nodeId || '');
    items.push({
      label: 'Open in Topology',
      to: `/topology?mode=live&sourceType=nas&sourceId=${encodeURIComponent(nodeId)}&routerId=${encodeURIComponent(nodeId)}&openDetail=1`,
    });
  }
  if (entry.siteId) items.push({ label: 'Open in Infrastructure', to: `/infrastructure?tab=sites&siteId=${encodeURIComponent(String(entry.siteId))}` });

  const seen = new Set();
  return items.filter((item) => {
    if (!item?.to || seen.has(item.to)) return false;
    seen.add(item.to);
    return true;
  }).slice(0, 3);
};

const toSearchHaystack = (entry) => (
  [
    entry?.id,
    entry?.message,
    entry?.actor,
    entry?.sourceType,
    entry?.sourceId,
    entry?.incidentId,
    entry?.ticketId,
    entry?.customerId,
    entry?.routerId,
    entry?.nodeId,
    entry?.siteId,
    entry?.actionType,
  ]
    .map((item) => String(item || '').toLowerCase())
    .join(' ')
);

const inTimeRange = (entry, range) => {
  if (range === 'latest') return true;
  const parsed = new Date(entry?.createdAt || '');
  if (Number.isNaN(parsed.getTime())) return false;
  const now = new Date();
  if (range === 'today') {
    return parsed.toDateString() === now.toDateString();
  }
  if (range === '7d') {
    const diff = now.getTime() - parsed.getTime();
    return diff >= 0 && diff <= (7 * 24 * 60 * 60 * 1000);
  }
  return true;
};

export default function AdminExecutionLogs() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [logs, setLogs] = useState([]);
  const [sourceFilter, setSourceFilter] = useState('all');
  const [actionFilter, setActionFilter] = useState('all');
  const [timeFilter, setTimeFilter] = useState('latest');
  const [search, setSearch] = useState('');
  const [selectedLogId, setSelectedLogId] = useState('');

  const loadLogs = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const payload = await fetchExecutionLogs({
        apiBase: API,
        navigate,
        params: { limit: 250 },
      });
      const items = Array.isArray(payload?.items)
        ? payload.items.slice().sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime())
        : [];
      setLogs(items);
    } catch (fetchError) {
      setLogs([]);
      setError(String(fetchError?.message || 'Gagal memuat execution log.'));
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const filteredLogs = useMemo(() => {
    const keyword = String(search || '').trim().toLowerCase();
    return logs.filter((entry) => {
      if (sourceFilter !== 'all' && formatSourceLabel(entry?.sourceType) !== sourceFilter) return false;
      if (actionFilter !== 'all' && String(entry?.actionType || '') !== actionFilter) return false;
      if (!inTimeRange(entry, timeFilter)) return false;
      if (keyword && !toSearchHaystack(entry).includes(keyword)) return false;
      return true;
    });
  }, [logs, sourceFilter, actionFilter, timeFilter, search]);

  useEffect(() => {
    if (!filteredLogs.length) {
      setSelectedLogId('');
      return;
    }
    if (!selectedLogId || !filteredLogs.some((entry) => String(entry.id) === String(selectedLogId))) {
      setSelectedLogId(String(filteredLogs[0].id || ''));
    }
  }, [filteredLogs, selectedLogId]);

  const selectedLog = useMemo(
    () => filteredLogs.find((entry) => String(entry.id) === String(selectedLogId)) || filteredLogs[0] || null,
    [filteredLogs, selectedLogId],
  );

  const summary = useMemo(() => {
    const actorCount = new Map();
    const actionCount = new Map();
    const sourceCount = new Map();

    filteredLogs.forEach((entry) => {
      const actor = String(entry.actor || '-');
      const action = String(entry.actionType || 'manual_note');
      const source = formatSourceLabel(entry.sourceType);
      actorCount.set(actor, (actorCount.get(actor) || 0) + 1);
      actionCount.set(action, (actionCount.get(action) || 0) + 1);
      sourceCount.set(source, (sourceCount.get(source) || 0) + 1);
    });

    const top = (map) => {
      const ranked = Array.from(map.entries()).sort((a, b) => b[1] - a[1]);
      return ranked[0] || null;
    };

    return {
      total: filteredLogs.length,
      latestActor: filteredLogs[0]?.actor || '-',
      topActor: top(actorCount),
      topAction: top(actionCount),
      topSource: top(sourceCount),
    };
  }, [filteredLogs]);

  return (
    <AppShell sidebarItems={commandSidebarItems} activePathname="/execution-logs" activeSearch="" initialTab="overview">
      <PageContainer>
        <PageHeader
          eyebrow="Operations"
          title="Execution Log"
          subtitle="Aktivitas operasional terbaru lintas Recovery, Tickets, Topology, Customers, dan Infrastructure."
          action={(
            <UIButton variant="ghost" onClick={loadLogs} disabled={loading}>
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </UIButton>
          )}
        />

        <div className="mb-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          <StatCard title="Log Terbaru" value={summary.total} note="Jumlah log sesuai filter aktif" tone={summary.total > 0 ? 'info' : 'neutral'} />
          <StatCard title="Actor Terakhir" value={summary.latestActor || '-'} note={summary.topActor ? `Dominan: ${summary.topActor[0]} (${summary.topActor[1]} log)` : (filteredLogs[0]?.createdAt ? new Date(filteredLogs[0].createdAt).toLocaleString('id-ID') : 'Belum ada log')} tone="neutral" />
          <StatCard title="Action Teratas" value={summary.topAction ? getExecutionActionLabel(summary.topAction[0]) : '-'} note={summary.topAction ? `${summary.topAction[1]} event` : 'Belum ada data'} tone="warn" />
          <StatCard title="Source Teraktif" value={summary.topSource ? String(summary.topSource[0]).toUpperCase() : '-'} note={summary.topSource ? `${summary.topSource[1]} event` : 'Belum ada data'} tone="ok" />
        </div>

        <SectionContainer className="mb-4">
          <div className="grid gap-2 lg:grid-cols-[180px_220px_180px_minmax(0,1fr)]">
            <select
              value={sourceFilter}
              onChange={(event) => setSourceFilter(String(event.target.value || 'all'))}
              className="rounded-lg border border-white/10 bg-[#0a1322] px-3 py-2 text-xs text-slate-100"
            >
              {sourceTypeOptions.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
            </select>
            <select
              value={actionFilter}
              onChange={(event) => setActionFilter(String(event.target.value || 'all'))}
              className="rounded-lg border border-white/10 bg-[#0a1322] px-3 py-2 text-xs text-slate-100"
            >
              {actionTypeOptions.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
            </select>
            <select
              value={timeFilter}
              onChange={(event) => setTimeFilter(String(event.target.value || 'latest'))}
              className="rounded-lg border border-white/10 bg-[#0a1322] px-3 py-2 text-xs text-slate-100"
            >
              {timeRangeOptions.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
            </select>
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Cari actor, message, ticket/customer/router/site..."
              className="rounded-lg border border-white/10 bg-[#0a1322] px-3 py-2 text-xs text-slate-100 placeholder:text-slate-500"
            />
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            {summary.topAction ? (
              <button
                type="button"
                onClick={() => setActionFilter(String(summary.topAction[0] || 'all'))}
                className="rounded-md border border-cyan-500/35 bg-cyan-500/10 px-2 py-1 text-[11px] text-cyan-100 hover:bg-cyan-500/20"
              >
                Quick Action: {getExecutionActionLabel(summary.topAction[0])}
              </button>
            ) : null}
            {summary.topSource ? (
              <button
                type="button"
                onClick={() => setSourceFilter(String(summary.topSource[0] || 'all'))}
                className="rounded-md border border-emerald-500/35 bg-emerald-500/10 px-2 py-1 text-[11px] text-emerald-100 hover:bg-emerald-500/20"
              >
                Quick Source: {String(summary.topSource[0] || '').toUpperCase()}
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => {
                setSourceFilter('all');
                setActionFilter('all');
                setTimeFilter('latest');
                setSearch('');
              }}
              className="rounded-md border border-white/15 bg-[#0a1322] px-2 py-1 text-[11px] text-slate-300 hover:border-white/35 hover:text-white"
            >
              Reset Filter
            </button>
          </div>
        </SectionContainer>

        {error ? (
          <SectionContainer className="mb-4 border border-rose-500/35 bg-rose-500/10 text-sm text-rose-200">
            {error}
          </SectionContainer>
        ) : null}

        <div className="grid gap-4 xl:grid-cols-[1.2fr_0.8fr]">
          <SectionContainer className="space-y-2">
            {loading ? <p className="text-sm text-slate-400">Masih memuat data...</p> : null}
            {!loading && filteredLogs.length === 0 ? (
              <p className="text-sm text-slate-400">{logs.length === 0 ? 'Belum ada execution log. Aktivitas operasional terbaru akan muncul di sini.' : 'Tidak ada log yang cocok dengan filter.'}</p>
            ) : null}
            {!loading && filteredLogs.length > 0 ? (
              <div className="space-y-2">
                {filteredLogs.map((entry) => {
                  const isActive = String(selectedLogId || '') === String(entry.id || '');
                  const primaryCta = buildPrimaryCta(entry);
                  return (
                    <button
                      key={entry.id || `${entry.createdAt}-${entry.message}`}
                      type="button"
                      onClick={() => setSelectedLogId(String(entry.id || ''))}
                      className={`w-full rounded-lg border px-3 py-2 text-left transition-colors ${
                        isActive
                          ? 'border-cyan-400/60 bg-cyan-500/10 shadow-[inset_3px_0_0_0_rgba(34,211,238,0.8)]'
                          : 'border-white/10 bg-[#0a1322] hover:border-white/25 hover:bg-[#101c31]'
                      }`}
                    >
                      <div className="mb-1 flex items-center justify-between gap-2">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <StatusBadge tone={executionActionTone(entry.actionType)}>{getExecutionActionLabel(entry.actionType)}</StatusBadge>
                          <StatusBadge tone={sourceTypeTone(entry.sourceType)}>{String(formatSourceLabel(entry.sourceType) || 'manual').toUpperCase()}</StatusBadge>
                        </div>
                        <p className="text-[11px] text-slate-500">{entry.createdAt ? new Date(entry.createdAt).toLocaleString('id-ID') : '-'}</p>
                      </div>
                      <p className="text-sm text-slate-100">{entry.message || '-'}</p>
                      <p className="mt-1 text-[11px] text-slate-500">Context: {formatSourceLabel(entry.sourceType)}:{entry.sourceId || '-'}</p>
                      <div className="mt-1 flex items-center justify-between gap-2">
                        <p className="text-[11px] text-slate-400">Actor: <span className="text-slate-200">{entry.actor || '-'}</span></p>
                        {primaryCta ? (
                          <span className="inline-flex items-center gap-1 text-[11px] text-cyan-300">
                            <ArrowUpRight className="h-3.5 w-3.5" />
                            {primaryCta.label}
                          </span>
                        ) : null}
                      </div>
                    </button>
                  );
                })}
              </div>
            ) : null}
          </SectionContainer>

          <SectionContainer>
            <p className="mb-2 text-xs uppercase tracking-[0.12em] text-slate-400">Log Detail</p>
            {!selectedLog ? (
              <p className="text-sm text-slate-400">Belum ada log terpilih.</p>
            ) : (
              <div className="space-y-2 text-sm text-slate-200">
                <div className="flex flex-wrap items-center gap-1.5">
                  <StatusBadge tone={executionActionTone(selectedLog.actionType)}>{getExecutionActionLabel(selectedLog.actionType)}</StatusBadge>
                  <StatusBadge tone={sourceTypeTone(selectedLog.sourceType)}>{String(formatSourceLabel(selectedLog.sourceType) || 'manual').toUpperCase()}</StatusBadge>
                </div>
                <p className="text-slate-100">{selectedLog.message || '-'}</p>
                <p className="text-xs text-slate-400">Actor: <span className="text-slate-200">{selectedLog.actor || '-'}</span></p>
                <p className="text-xs text-slate-400">Waktu: <span className="text-slate-200">{selectedLog.createdAt ? new Date(selectedLog.createdAt).toLocaleString('id-ID') : '-'}</span></p>
                <div className="space-y-1 rounded-md border border-white/10 bg-[#0a1322] px-2.5 py-2 text-xs">
                  <p className="text-slate-400">Context</p>
                  <p>source: <span className="text-slate-200">{formatSourceLabel(selectedLog.sourceType)}:{selectedLog.sourceId || '-'}</span></p>
                  <p>incident: <span className="text-slate-200">{selectedLog.incidentId || '-'}</span></p>
                  <p>ticket: <span className="text-slate-200">{selectedLog.ticketId || '-'}</span></p>
                  <p>router/node: <span className="text-slate-200">{selectedLog.routerId || selectedLog.nodeId || '-'}</span></p>
                  <p>customer: <span className="text-slate-200">{selectedLog.customerId || '-'}</span></p>
                  <p>site: <span className="text-slate-200">{selectedLog.siteId || '-'}</span></p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {buildSecondaryCtas(selectedLog).map((cta) => (
                    <UIButton key={cta.to} as="link" to={cta.to} variant="ghost">{cta.label}</UIButton>
                  ))}
                </div>
              </div>
            )}
          </SectionContainer>
        </div>
      </PageContainer>
    </AppShell>
  );
}
